<script setup>
/* Imports */
import {
  reactive,
  computed,
  onBeforeMount,
  onBeforeUnmount,
} from 'vue';
import {
  useRouter,
  useRoute,
} from 'vue-router';
import axios from 'axios';
import { sendAnalyticsPageView } from '@garmin/analytics';
import Heading from '@garmin/components-global-vue-heading';

/* Store */
import useGlobalStore from '../store/globalStore.js';

/* Helpers */
import { translateWithKeys } from '../helpers/translate.js';

/* Composables */
import { useBackToTopBtn } from '../composables/useBackToTopBtn.js';

/* Components */
import CoursesCardContainer from '../containers/CoursesCardContainer.vue';
import BackToTopBtn from '../components/BackToTopBtn.vue';

/* Initialize - router and route */
const router = useRouter();
const route = useRoute();

/* Inject composables - BackToTop */
const {
  backToTopBtnRef,
  showBackToTopBtn,
} = useBackToTopBtn();

/* Store */
const globalStore = useGlobalStore();
const {
  locale,
  translations,
  countries,
} = globalStore;

const ssrMeta = globalStore.getSSRSearchMeta;
const ssrReady = !!ssrMeta;

const state = reactive({
  all_ready: ssrReady,

  hasMoreRecords: ssrReady ? ssrMeta.hasMoreRecords : true,

  totalRecords: ssrReady ? ssrMeta.totalRecords : 0,

  loadedIndex: ssrReady ? ssrMeta.loadedIndex : 0,

  queryCourses: [],

  notify_timeout: undefined,

  header_course_search: route.query.q ? decodeURI(route.query.q) : '',
  header_address_search: route.query.addr ? decodeURI(route.query.addr) : '',
  header_selected_country: route.query.a2 || globalStore.getCurrentCountry,
  header_selected_state: '',
  header_new_checkbox: route.query.new === 'true',
  header_updated_checkbox: route.query.upd === 'true',
  header_contours_checkbox: route.query.cnt === 'true',

  show_notify: false,

  typing_timeout: undefined,

  noResults: computed(() => state.all_ready && globalStore.getCourses.length === 0),

  isLoading: computed(() => !state.all_ready),

  results_count: computed(() => translateWithKeys(translations.results, {
    NumberOfResults: state.totalRecords,
  })),

  cdnCache: true,
});

if (ssrReady && route.query.state) {
  const uriState = decodeURI(route.query.state);
  if ((globalStore.getStates || []).includes(uriState)) {
    state.header_selected_state = uriState;
  }
}

// SSR-safe accessors: guarded so touching setup scope can't crash SSR render.
const isClient = () => typeof window !== 'undefined';
const getOrigin = () => (isClient() ? window.location.origin : '');
const getLocationSearch = () => (isClient() ? window.location.search : '');

const setCacheBypass = (url) => {
  // add cdncache param to all calls to bypass cache if set in the page url
  let newUrl = url;
  if (getLocationSearch().includes('cdncache=false')) {
    state.cdnCache = false;

    const urlObj = new URL(url);
    urlObj.searchParams.set('cdncache', 'false');
    newUrl = urlObj.href;
  }

  return newUrl;
};

const fetchCourses = (options) => {
  const url = `${getOrigin()}/golf-courses/api/courses`;
  const newUrl = new URL(setCacheBypass(url));
  newUrl.searchParams.set('country', options.country || '');
  newUrl.searchParams.set('state', options.state || '');
  newUrl.searchParams.set('new', options.new || false);
  newUrl.searchParams.set('updated', options.updated || false);
  newUrl.searchParams.set('contours', options.contours || false);
  newUrl.searchParams.set('address', options.address || '');
  newUrl.searchParams.set('search', options.search || '');
  newUrl.searchParams.set('size', options.size || 100);
  newUrl.searchParams.set('from', options.from || 0);

  return axios.get(newUrl.href).catch((err) => {
    throw new Error(err);
  });
};

const fetchStates = () => {
  const url = `${getOrigin()}/golf-courses/api/states?country=${state.header_selected_country}&locale=${locale}`;
  const newUrl = setCacheBypass(url);

  return axios.get(newUrl).catch((err) => {
    throw new Error(err);
  });
};

const refreshStateOptions = async () => {
  if (state.header_selected_country) {
    globalStore.setCurrentCountry(state.header_selected_country);
    globalStore.setStates([]);
    const states = await fetchStates();
    if (states.data) {
      globalStore.setStates(states.data.sort());
    }
  }
  state.header_selected_state = '';
};

const refreshSearchResults = () => {
  const options = {
    country: state.header_selected_country,
    state: state.header_selected_state,
    new: state.header_new_checkbox,
    updated: state.header_updated_checkbox,
    contours: state.header_contours_checkbox,
    address: state.header_address_search,
    search: state.header_course_search,
    size: 12,
    from: 0,
  };

  fetchCourses(options).then((result) => {
    globalStore.setCourses(result.data.courses);
    state.loadedIndex = result.data.to;
    state.hasMoreRecords = !result.data.end;
    state.totalRecords = result.data.total;
  });
};

const onHeaderChange = () => {
  const hasCdnCacheParam = getLocationSearch().includes('cdncache');

  const routerQueries = {
    a2: state.header_selected_country,
    state: encodeURI(state.header_selected_state),
    new: state.header_new_checkbox,
    upd: state.header_updated_checkbox,
    cnt: state.header_contours_checkbox,
    q: encodeURI(state.header_course_search),
    addr: encodeURI(state.header_address_search),
  };

  if (hasCdnCacheParam) {
    routerQueries.cdncache = state.cdnCache;
  }
  router.replace({
    path: `/${locale}/golf-courses/`,
    query: routerQueries,
  });
};

const coursesLoadedCallback = async () => {
  /* Load header state from url query parameters, if they exist */
  state.header_new_checkbox = (route.query.new === 'true');
  state.header_updated_checkbox = (route.query.upd === 'true');
  state.header_contours_checkbox = (route.query.cnt === 'true');
  state.header_course_search = (route.query.q) ? decodeURI(route.query.q) : '';
  state.header_address_search = (route.query.addr) ? decodeURI(route.query.addr) : '';

  /* Refresh the relevant state/region options */
  const uriState = decodeURI(route.query.state);

  await refreshStateOptions();
  state.header_selected_state = (
    route.query.state
    && (globalStore.getStates || []).includes(uriState)
  ) ? decodeURI(uriState) : '';

  /* Manually call on_header_change to initialize header and URL state */
  onHeaderChange();

  /* Load some golf courses */
  state.all_ready = true;
};

const typingTimeoutSubmit = () => {
  clearTimeout(state.typing_timeout);
  state.typing_timeout = setTimeout(() => refreshSearchResults(), 100);
};

const onCourseSearchChange = () => {
  typingTimeoutSubmit();
  onHeaderChange();
};

const onCountrySelect = async () => {
  await refreshStateOptions();
  refreshSearchResults();
  onHeaderChange();
};

const onStateSelect = () => {
  refreshSearchResults();
  onHeaderChange();
};

const onNewChecked = () => {
  refreshSearchResults();
  onHeaderChange();
};

const onNewKeyboard = () => {
  state.header_new_checkbox = !state.header_new_checkbox;
  onNewChecked();
};

const onUpdatedChecked = () => {
  refreshSearchResults();
  onHeaderChange();
};

const onUpdatedKeyboard = () => {
  state.header_updated_checkbox = !state.header_updated_checkbox;
  onUpdatedChecked();
};

const onContoursChecked = () => {
  refreshSearchResults();
  onHeaderChange();
};

const onContoursKeyboard = () => {
  state.header_contours_checkbox = !state.header_contours_checkbox;
  onContoursChecked();
};

const onClipboardCopy = () => {
  clearTimeout(state.notify_timeout);
  state.notify_timeout = setTimeout(() => {
    state.show_notify = false;
  }, 700);
  state.show_notify = true;
};

const notifyClasses = computed(() => ({
  'main-page__notify': true,
  'main-page__notify-active': state.show_notify,
}));

const onClearHeader = () => {
  state.header_new_checkbox = false;
  state.header_updated_checkbox = false;
  state.header_contours_checkbox = false;
  state.header_course_search = '';
  state.header_address_search = '';
  state.header_selected_country = '';
  refreshStateOptions();
  refreshSearchResults();
  onHeaderChange();
};

const onLoadMore = () => {
  const options = {
    country: state.header_selected_country,
    state: state.header_selected_state,
    new: state.header_new_checkbox,
    updated: state.header_updated_checkbox,
    contours: state.header_contours_checkbox,
    address: state.header_address_search,
    search: state.header_course_search,
    size: 12,
    from: state.loadedIndex + 1,
  };

  fetchCourses(options).then((result) => {
    globalStore.setCourses([...globalStore.getCourses, ...result.data.courses]);

    state.loadedIndex = result.data.to;
    state.hasMoreRecords = !result.data.end;
  });
};

onBeforeMount(() => {
  // Title is already rendered server-side from translations via SEO locals.
  document.addEventListener('copy', onClipboardCopy);

  const pageViewAnalyticsData = {
    tealium_event: 'page_view',
  };

  window.addEventListener('utag-ready', () => {
    sendAnalyticsPageView(pageViewAnalyticsData);
  });

  if (ssrReady) {
    globalStore.setSSRSearchMeta(null);
    onHeaderChange();
    return;
  }

  state.header_selected_country = route.query.a2 || route.params.locale.split('-')[1];

  const options = {
    country: state.header_selected_country,
    state: route.query.state,
    new: route.query.new === 'true',
    updated: route.query.upd === 'true',
    contours: route.query.cnt === 'true',
    address: route.query.addr,
    search: route.query.q,
    size: 12,
    from: 0,
  };

  fetchCourses(options).then((result) => {
    globalStore.setCourses(result.data.courses);

    state.loadedIndex = result.data.to;
    state.hasMoreRecords = !result.data.end;
    state.totalRecords = result.data.total;

    coursesLoadedCallback();
  }).catch(() => {
    globalStore.setCourses([]);
    coursesLoadedCallback();
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('copy', onClipboardCopy);
  clearTimeout(state.typing_timeout);
  clearTimeout(state.notify_timeout);
});
</script>

<template>
  <div
    ref="backToTopBtnRef"
    class="main-page__back-to-top-btn"
  />
  <div class="main-page__golf-course-image">
    <Heading
      size="1"
      class="main-page__page-title"
      theme="dark"
      :dataTestId="{
        host: 'main-page-header',
      }"
    >
      <h1
        data-testid="main-page-header-content"
        v-text="translations.page_title"
      />
    </Heading>
  </div>

  <div class="main-page__header">
    <div class="main-page__header-section main-page__course-search">
      <g-input
        label="Search Course"
        hide-label="true"
        label-for="course-search"
      >
        <input
          id="course-search"
          v-model="state.header_course_search"
          name="course-search"
          :placeholder="translations.course_name_location"
          type="text"
          @keyup="onCourseSearchChange"
        >
      </g-input>
    </div>

    <div class="main-page__header-section main-page__country-picker-section">
      <g-drop-down
        label="Country"
        hide-label="true"
      >
        <select
          v-model="state.header_selected_country"
          name="Country"
          :aria-label="translations.country_region"
          @change="onCountrySelect"
        >
          <option value="">
            {{ translations.country_region }}
          </option>
          <option
            v-for="country in countries"
            :key="country[0]"
            :value="country[0]"
          >
            {{ country[1] }}
          </option>
        </select>
      </g-drop-down>
    </div>

    <div class="main-page__header-section main-page__state-picker-section">
      <g-drop-down
        label="State/Region"
        hide-label="true"
      >
        <select
          v-model="state.header_selected_state"
          name="State/Region"
          :aria-label="translations.state_region"
          @change="onStateSelect"
        >
          <option value="">
            {{ translations.state_region }}
          </option>
          <option
            v-for="stateName in globalStore.getStates"
            :key="stateName"
            :value="stateName"
          >
            {{ stateName }}
          </option>
        </select>
      </g-drop-down>
    </div>

    <div class="main-page__header-section main-page__checkbox-section">
      <label class="main-page__checkbox-label">
        <g-switch
          size="large"
          :left-label="translations.new"
          label-position="right"
          @keydown.enter="onNewKeyboard"
        >
          <input
            v-model="state.header_new_checkbox"
            type="checkbox"
            name="new"
            :aria-label="translations.new"
            @change="onNewChecked"
          >
        </g-switch>
      </label>
    </div>

    <div class="main-page__header-section main-page__checkbox-section">
      <label class="main-page__checkbox-label">
        <g-switch
          size="large"
          :left-label="translations.updated"
          label-position="right"
          @keydown.enter="onUpdatedKeyboard"
        >
          <input
            v-model="state.header_updated_checkbox"
            type="checkbox"
            name="updated"
            :aria-label="translations.updated"
            @change="onUpdatedChecked"
          >
        </g-switch>
      </label>
    </div>

    <div class="main-page__header-section main-page__checkbox-section">
      <label class="main-page__checkbox-label">
        <g-switch
          size="large"
          :left-label="translations.contours"
          label-position="right"
          @keydown.enter="onContoursKeyboard"
        >
          <input
            v-model="state.header_contours_checkbox"
            type="checkbox"
            name="contours"
            :aria-label="translations.contours"
            @change="onContoursChecked"
          >
        </g-switch>
      </label>
    </div>
  </div>

  <div class="main-page__spacer" />

  <div class="main-page__content-wrapper">
    <div class="main-page__number-of-results-wrapper">
      <span>{{ state.results_count }}</span>
      <a
        class="main-page__clear-results-text"
        tabindex="0"
        @click="onClearHeader()"
        @keydown.enter="onClearHeader()"
      >
        {{ translations.reset_results }}
      </a>
    </div>

    <div id="show-back-to-top" />

    <CoursesCardContainer
      :courses="globalStore.getCourses"
    />

    <div
      v-if="state.noResults"
      class="main-page__no-results-found-wrapper"
    >
      <span>{{ translations.no_results }}</span>
    </div>

    <div
      v-if="state.isLoading"
      class="main-page__loader-wrapper"
    >
      <g-loader
        type="default"
        class="main-page__loader-wrapper__spinner"
      />
    </div>

    <div :class="notifyClasses">
      <div>
        <span>{{ translations.address_copied }}</span>
      </div>
    </div>

    <div
      v-if="state.hasMoreRecords"
      class="main-page__more-button-wrapper"
    >
      <g-show-more
        v-if="state.hasMoreRecords"
        id="load-more"
        class="main-page__more-button-wrapper__bottom-button"
        :text="translations.load_more_courses"
        @click="onLoadMore"
      />
    </div>

    <div class="main-page__bottom-links-wrapper">
      <span>
        <a
          class="main-page__bottom-link"
          :href="`https://www.garmin.com/${locale}/forms/golfcourses/`"
        >
          {{ translations.report_or_request }}
        </a>
      </span>
      <br>
      <span>
        <a
          class="main-page__bottom-link"
          :href="`https://www.garmin.com/${locale}/maps/updates/golf/`"
        >
          {{ translations.course_updates }}
        </a>
      </span>

      <BackToTopBtn v-if="showBackToTopBtn" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-page {

  &__spacer {
    height: 32px;
  }

  &__golf-course-image {
    @include golf-course-image(
      '@/assets/golf-course-240.jpg',
      '@/assets/golf-course-480.jpg',
      '@/assets/golf-course-960.jpg',
      '@/assets/golf-course-1920.jpg'
    );
  }

  &__page-title {
    margin-top: -2rem;
  }

  &__header {
    z-index: 99;
    padding: 0 1rem;
    width: calc(100% - 2rem);
    max-width: calc(1200px - 2rem);
    height: 5rem;
    background-color: $color-white;
    /* stylelint-disable-next-line color-no-hex */
    box-shadow: 0 0.25rem 0.25rem #bdbdbd;
    border-radius: 0.312rem;
    margin: 0 auto;
    margin-top: -2.5rem;
    display: flex;
    flex-flow: nowrap row;
    justify-content: space-around;
    overflow: hidden;

    &-section {
      display: flex;
      align-items: center;
      font-size: 1.3rem;
      /* stylelint-disable-next-line color-no-hex */
      color: #323232;
      @include font-primary();
      margin-left: 0.5rem;
      margin-right: 0.5rem;
      overflow: hidden;
      white-space: nowrap;

      .g__input input {
        padding: 10px;
        font-size: 14px;
      }

      /* stylelint-disable selector-type-no-unknown */
      g-input,
      g-drop-down {
        width: 100%;
      }
      /* stylelint-enable selector-type-no-unknown */

      :deep(.g__dropdown select) {
        font-size: 0.875rem;
        padding: 0.625rem 2rem 0.625rem 0.5rem;
        margin: 0.3rem 0.125rem 0.3rem 0.125rem;
        outline: none;
        color: $color-black;
      }
    }
  }

  &__checkbox-section {
    overflow: visible;

    .g__switch {
      padding: 0 2px;
    }
  }

  &__course-search,
  &__country-picker-section,
  &__state-picker-section {
    width: 25%;
  }

  &__checkbox-label {
    font-family: inherit;
    font-size: 1rem;
  }

  /* Handle the loading screen */
  &__loader-wrapper {
    margin: 0;
    position: fixed;
    z-index: 1001;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(255, 255, 255, 0.5);

    &__spinner {
      width: 150px;
      position: absolute;
      top: 50%;
      left: 50%;
      margin-right: -50%;
      transform: translate(-50%, -50%);
    }
  }

  &__number-of-results-wrapper {
    text-align: right;
    padding-right: 1rem;
    font-size: 1rem;
    cursor: default;
    max-width: 1200px;
    margin: 0 auto;
  }

  &__no-results-found-wrapper {
    text-align: center;
    padding: 1rem 0 2rem 0;
    font-size: 1rem;
    cursor: default;
  }

  &__clear-results-text {
    color: $color-gray-20;
    margin-left: 1rem;
  }

  &__clear-results-text,
  &__bottom-link {

    &:hover {
      color: $color-black;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  &__bottom-link {
    display: inline-block;
    padding: ($spacing-xs * 3) 0;
    margin-bottom: $spacing-sm;
  }

  &__more-button-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;

    :deep(.g__button__host) {
      margin-bottom: 0;
    }

    &__bottom-button {
      padding: 0.5rem 2rem;
      margin: 1rem 2rem;
    }
  }

  &__notify {
    z-index: 9999;
    position: fixed;
    bottom: -4rem;
    left: calc(50% - 6rem);
    width: 12rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: $color-white;
    background-color: $color-garmin-blue-30;
    @include font-primary-weight-bold();
    box-shadow: none;
    transition: all 0.1s ease-out;

    &-active {
      bottom: 0;
    }
  }

  &__more-icon {
    width: 0.7rem;
    height: 0.625rem;
    margin: 0.2rem 0 0 0.6rem;
  }

  &__bottom-links-wrapper {
    padding-bottom: 2rem;
    text-align: right;
    padding-right: 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  &__content-wrapper {
    margin: 0 auto;
    padding: 0 1rem;
  }
}

@media only screen and (max-width: 900px) {

  .main-page {
    &__header {
      flex-wrap: wrap;
      height: auto;
      min-height: 5rem;
      padding-top: 16px;

      &-section {
        margin: 5px 0;
        height: 50px;
        box-sizing: border-box;
      }
    }

    &__course-search,
    &__country-picker-section,
    &__state-picker-section {
      width: 100%;
    }

    &__header-section .g__input input {
      padding: 8px;
      box-sizing: initial;
    }

    &__header-section .g__dropdown select {
      height: 50px;
    }

    &__checkbox-section {
      width: 33%;
    }
  }

  input[type = 'text'],
  input[type = 'checkbox'],
  select {
    height: 2rem;
  }

  select,
  .main-page__checkbox-label,
  input[type = 'text'] {
    font-size: 1rem;
  }
}

@media only screen and (max-width: 500px) {

  .main-page {
    &__checkbox-section {
      width: 100%;
    }
  }
}
</style>
